import React from "react";
import {
  Clock,
  PlayCircle,
  CheckCircle2,
  AlertTriangle,
  FilterX,
  FileCheck2,
  HardDriveDownload,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import type { CourtScraperKpis } from "@/redux/api/courtScraper.rtkq";

interface KpiCardsProps {
  kpis?: CourtScraperKpis;
  isLoading?: boolean;
}

export const KpiCards: React.FC<KpiCardsProps> = ({ kpis, isLoading }) => {
  if (isLoading || !kpis) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {Array.from({ length: 7 }).map((_, i) => (
          <Card key={i} className="animate-pulse bg-white border-slate-200">
            <CardContent className="p-4 space-y-2">
              <div className="h-4 bg-slate-200 rounded w-3/4"></div>
              <div className="h-7 bg-slate-200 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const totalConsolidated = Number(kpis.total_consolidated || 0);
  const completed = Number(kpis.nlp_completed || 0);
  const inFlight = Number(kpis.nlp_pending || 0) + Number(kpis.nlp_running || 0);
  const awaiting = Number(kpis.awaiting_nlp || 0);
  const failed = Number(kpis.nlp_failed || 0);
  const noise = Number(kpis.nlp_noise || 0) + Number(kpis.nlp_skipped || 0);

  const completedPct = totalConsolidated > 0 ? ((completed / totalConsolidated) * 100).toFixed(1) : "0";
  const inFlightPct = totalConsolidated > 0 ? ((inFlight / totalConsolidated) * 100).toFixed(1) : "0";
  const awaitingPct = totalConsolidated > 0 ? ((awaiting / totalConsolidated) * 100).toFixed(1) : "0";
  const failedPct = totalConsolidated > 0 ? ((failed / totalConsolidated) * 100).toFixed(1) : "0";

  const cards = [
    {
      title: "סה\"כ מסמכים שאוחדו",
      value: totalConsolidated.toLocaleString(),
      subtitle: "מוכנים לעיבוד NLP",
      icon: FileCheck2,
      color: "text-indigo-600",
      bg: "bg-indigo-50",
      border: "border-indigo-100",
    },
    {
      title: "הושלמו בהצלחה",
      value: completed.toLocaleString(),
      subtitle: `${completedPct}% מתוך שאוחדו`,
      icon: CheckCircle2,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
      border: "border-emerald-100",
    },
    {
      title: "בעיבוד ובתור (NLP)",
      value: inFlight.toLocaleString(),
      subtitle: `${inFlightPct}% בתהליך`,
      icon: PlayCircle,
      color: "text-blue-600",
      bg: "bg-blue-50",
      border: "border-blue-100",
    },
    {
      title: "ממתינים לתור NLP",
      value: awaiting.toLocaleString(),
      subtitle: `${awaitingPct}% ממתינים`,
      icon: Clock,
      color: "text-amber-600",
      bg: "bg-amber-50",
      border: "border-amber-100",
    },
    {
      title: "נכשלו ב-NLP",
      value: failed.toLocaleString(),
      subtitle: `${failedPct}% דורשים בדיקה`,
      icon: AlertTriangle,
      color: "text-rose-600",
      bg: "bg-rose-50",
      border: "border-rose-100",
    },
    {
      title: "רעש / סוננו",
      value: noise.toLocaleString(),
      subtitle: "החלטות ללא תוכן מהותי",
      icon: FilterX,
      color: "text-slate-600",
      bg: "bg-slate-50",
      border: "border-slate-200",
    },
    {
      title: "הורדות PDF תקינות",
      value: Number(kpis.downloaded_pdfs || 0).toLocaleString(),
      subtitle: `מתוך ${Number(kpis.total_documents || 0).toLocaleString()} מסמכים`,
      icon: HardDriveDownload,
      color: "text-cyan-600",
      bg: "bg-cyan-50",
      border: "border-cyan-100",
    },
  ];

  return (
    <div className="space-y-4">
      {/* Metric Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {cards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <Card
              key={idx}
              className={`border ${card.border} shadow-sm hover:shadow transition-shadow bg-white`}
            >
              <CardContent className="p-3.5 flex flex-col justify-between h-full">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs font-medium text-slate-600 leading-tight">
                    {card.title}
                  </span>
                  <div className={`p-1.5 rounded-md ${card.bg}`}>
                    <Icon className={`w-4 h-4 ${card.color}`} />
                  </div>
                </div>
                <div>
                  <div className={`text-xl font-bold ${card.color}`}>
                    {card.value}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                    {card.subtitle}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Visual Pipeline Funnel Progress Bar */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
            <div className="text-sm font-semibold text-slate-800">
              סטטוס משפך עיבוד NLP (מתוך {totalConsolidated.toLocaleString()} מסמכים שאוחדו)
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                הושלמו: {completedPct}%
              </span>
              <span className="inline-flex items-center gap-1 text-blue-700">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                בעיבוד: {inFlightPct}%
              </span>
              <span className="inline-flex items-center gap-1 text-amber-700">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                ממתינים: {awaitingPct}%
              </span>
              <span className="inline-flex items-center gap-1 text-rose-700">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span>
                נכשלו: {failedPct}%
              </span>
            </div>
          </div>

          <div className="w-full h-3.5 bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
            <div
              style={{ width: `${completedPct}%` }}
              className="bg-emerald-500 transition-all duration-500 hover:opacity-90"
              title={`הושלמו: ${completed.toLocaleString()} (${completedPct}%)`}
            />
            <div
              style={{ width: `${inFlightPct}%` }}
              className="bg-blue-500 transition-all duration-500 hover:opacity-90"
              title={`בעיבוד: ${inFlight.toLocaleString()} (${inFlightPct}%)`}
            />
            <div
              style={{ width: `${awaitingPct}%` }}
              className="bg-amber-400 transition-all duration-500 hover:opacity-90"
              title={`ממתינים: ${awaiting.toLocaleString()} (${awaitingPct}%)`}
            />
            <div
              style={{ width: `${failedPct}%` }}
              className="bg-rose-500 transition-all duration-500 hover:opacity-90"
              title={`נכשלו: ${failed.toLocaleString()} (${failedPct}%)`}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
